import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import API_URLS from "@constants/api.urls";
import { TECHNOLOGIES } from "@constants/global.constants";

import useGlobalDataHandler from "@hooks/useGlobalDataHandler";

import DupontLogger from "@utils/DupontLogger";

import WebPubSubService from "@services/webPubSubService";

import { selectAllRONodes } from "@features/RO/store/ROConfig.selectors";
import { setShouldRecalculateFlowCalculator } from "@features/RO/store/ROConfig.slice";

import { getRequest } from "@api/index";

const Logger = DupontLogger("useWebPubSub");

/**
 * Map of WebSocket event types to their handler functions.
 * Each handler receives (dispatch, payload) and updates Redux accordingly.
 *
 * Add new event types here as needed:
 *   EVENT_TYPE: (dispatch, payload) => { dispatch(someAction(payload)); }
 */
/**
 * Hook that listens to WebPubSub messages and dispatches Redux actions.
 * Must be used in a component that stays mounted (e.g., App.js).
 */
const eventHandlers = {
  SEND_EXP_ELEMENT_TO_USER: (_, payload, refreshROElements) => {
    Logger.info("Handling SEND_EXP_ELEMENT_TO_USER event.", payload);
    refreshROElements();
  },

  DELETE_EXP_ELEMENT_FROM_USER: (_, payload, refreshROElements) => {
    Logger.info("Handling DELETE_EXP_ELEMENT_FROM_USER event.", payload);
    refreshROElements();
  },

  TRIGGER_FLOW_CALCULATOR: async (dispatch, payload, refreshROElements, roNodeIds) => {
    Logger.info("Handling TRIGGER_FLOW_CALCULATOR event.", payload);
    await refreshROElements();

    if (!roNodeIds.length) {
      Logger.info("No RO nodes found to recalculate flow calculator.");
      return;
    }

    roNodeIds.forEach(roNodeId => {
      dispatch(setShouldRecalculateFlowCalculator({ nodeId: roNodeId, shouldRecalculateFlowCalculator: true }));
    });
  },
};

export const useWebPubSubListener = () => {
  const dispatch = useDispatch();
  const allRONodes = useSelector(selectAllRONodes);
  const systemDesignNodes = useSelector(state => state.systemDesign.nodes);
  const { fetchROElements } = useGlobalDataHandler();
  const treatmentByNodeId = new Map((systemDesignNodes || []).map(node => [node.id, node?.data?.treatment]));
  const roNodeIds = Object.keys(allRONodes || {}).filter(nodeId => treatmentByNodeId.get(nodeId) === TECHNOLOGIES.RO);

  const refreshROElements = useCallback(async () => {
    try {
      await fetchROElements(true);
    } catch (error) {
      Logger.error("Failed to refresh RO elements:", error);
    }
  }, [fetchROElements]);

  const handleMessage = useCallback(
    message => {
      Logger.info("WebPubSub received message:", message);

      const type = message?.MessageHeader;
      const payload = message?.Body;

      if (!type) {
        Logger.warn("WebPubSub message missing event type:", message);
        return;
      }

      const handler = eventHandlers[type];
      if (handler) {
        Promise.resolve(handler(dispatch, payload, refreshROElements, roNodeIds)).catch(error => {
          Logger.error(`WebPubSub handler failed for event type: ${type}`, error);
        });
      } else {
        Logger.info(`WebPubSub unhandled event type: ${type}`, payload);
      }
    },
    [dispatch, refreshROElements, roNodeIds],
  );

  useEffect(() => {
    const unsubscribe = WebPubSubService.addMessageListener(handleMessage);
    return () => unsubscribe();
  }, [handleMessage]);
};

/**
 * Hook that provides connect/disconnect functions for WebPubSub.
 * Can be used in any component (e.g., SSOCallback).
 */
const useWebPubSub = () => {
  const connectWebPubSub = useCallback(async () => {
    try {
      const { data } = await getRequest(API_URLS.webPubSubscribe);
      const webSocketUrl = data?.url;
      if (webSocketUrl) {
        WebPubSubService.connect(webSocketUrl);
      } else {
        Logger.error("WebPubSub: No URL returned from subscribe API");
      }
    } catch (error) {
      Logger.error("WebPubSub subscribe error:", error);
    }
  }, []);

  const disconnectWebPubSub = useCallback(() => {
    WebPubSubService.disconnect();
  }, []);

  return { connectWebPubSub, disconnectWebPubSub };
};

export default useWebPubSub;
